import React, { ReactNode, useCallback } from 'react'
import { Box, styled, useMediaQuery, useTheme } from '@mui/material'
import PlaceholderImage from '../../../../assets/images/image-placeholder.png'
import { TestIds } from '../../../../testIds'
import { Product, selectItemNumbers } from '../../storage'
import { useSelector } from 'react-redux'
import {
  selectContactInfo,
  selectCurrentFarmerAccount,
} from '../../../azure-b2c/storage/auth.slice'
import CartSlice from '../../../cart/storage/cart.slice'
import { CartItem } from '../../../cart/storage/cart.types'
import { useAppDispatch } from '../../../../store/rootStore'
import { toast } from 'react-toastify'
import { ProductVariantDetails } from './ProductVariants'
import { ProductHeaderDetails } from './ProductHeaderDetails'
import HelperMessage from '../../../../components/HelperMessage'
import { ActionButton } from './ActionButton'
import { RemoveButton } from './RemoveButton'
import { isCartItem } from '../../../cart/utils/isCartItem'
import { ItemContainer, ItemLabel } from '../../../../components/styled'
import { ImageWithPlaceholder } from '../../../../components/ImageWithPlaceholder'
import dayjs from 'dayjs'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import { useQuoteModal } from '../../../quotes/components/QuoteModal/useQuoteModal'
import { getProductPrices } from '../../utils/getPrices'
import { isValidDate, saveScrollYPosition } from '../../../../util/helpers'
import { useProductStatus } from '../../utils/useProductStatus'
import Card, { CardAction, CardTitle } from '../../../../components/Card'
import { useLocation, useNavigate } from 'react-router-dom'
import Typography from '../../../../components/Typography'
import { AlternativeProduct } from './AlternativeProduct'
import { ScrollYPositionKeys } from '../../../../util/consts'
import { QuoteModalType } from '../../../quotes/components/QuoteModal/types'
import { openProductDetailsLink } from '../ProductDetails/utils/openProductDetails'
import { ProductType } from './types'

export const ProductCardContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})

export const ProductCardContent = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  backgroundColor: theme.colors.shade0,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },

  transition: 'padding 1s ease',
}))

export const ProductContentContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))

export const TopDownWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  margin: '16px',
  gap: '8px',
  width: '100%',
  [theme.breakpoints.down('sm')]: {
    width: 'auto',
  },
}))

export const ImageContainer = styled(ImageWithPlaceholder)(({ theme }) => ({
  backgroundColor: theme.colors.shade0,
  width: '100px',
  minWidth: '100px',
  height: '100px',
  [theme.breakpoints.down('sm')]: {
    width: '80px',
    minWidth: '80px',
    height: '80px',
  },
}))

export const ImageWrapper = styled('div')(({ theme }) => ({
  margin: '16px 0 0 16px',
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    margin: '16px 16px 0 16px',
  },
}))

export const CounterView = styled(Box)(({ theme }) => ({
  margin: 'auto 0px 0px auto',
  [theme.breakpoints.down('sm')]: {
    margin: '0 16px 16px 16px',
  },
}))

export const DiscountBanner = styled(Box)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.secondary700,
  padding: '0.5rem',
  paddingLeft: '1rem',
  ...theme.typography.body2,
  fontWeight: '900',
  color: theme.colors.shade0,
}))

export interface ProductCardProps {
  item: Product | CartItem
  disableLink?: boolean
  isPastProduct?: boolean
  isAlternativeProduct?: boolean
  showImage?: boolean
  showRemove?: boolean
  enableQuote?: boolean
  condensed?: boolean
  testId?: string
  renderSideContainer?: ReactNode
  renderQuickOrderButton?: ReactNode
}

export const ProductCardParent = ({
  item,
  disableLink,
  isPastProduct,
  isAlternativeProduct,
  showImage = true,
  showRemove,
  enableQuote,
  condensed,
  testId,
  renderSideContainer,
  renderQuickOrderButton,
}: ProductCardProps) => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const quoteProvider = useQuoteModal()

  const lastOrderedDate = isPastProduct
    ? new Date(Math.max(...item.orders.map((x) => new Date(x.createdAt ?? Date.now()).getTime())))
    : undefined

  const dispatch = useAppDispatch()
  const currentAccount = useSelector(selectCurrentFarmerAccount)
  const contactInfo = useSelector(selectContactInfo)
  const [isPreviouslyOrdered, setIsPreviouslyOrdered] = React.useState(false)

  const { isDisabled, isEndOfLife, noPriceAvailable } = useProductStatus(
    item.lifeCycleState,
    item.basePriceExcGst,
  )
  const alternativeProductState = useProductStatus(
    item.alternativeProduct?.lifeCycleState,
    item.alternativeProduct?.basePriceExcGst,
  )

  const isAllProduct = React.useMemo(
    () => !isPastProduct && !isAlternativeProduct,
    [isAlternativeProduct, isPastProduct],
  )

  const productType = React.useMemo(() => {
    if (isAlternativeProduct) return ProductType.Alternative
    else if (isPastProduct) return ProductType.Past
    else return ProductType.All
  }, [isAlternativeProduct, isPastProduct])

  const itemNumbers = useSelector(selectItemNumbers)

  const productDetailsNavigate = useCallback(() => {
    openProductDetailsLink(item, isAllProduct, productType, navigate)
  }, [item, isAllProduct, productType])

  const handleClickOnTitle = (e: React.MouseEvent<HTMLElement, MouseEvent> | null) => {
    e?.preventDefault()

    const sessionStorageKey = ScrollYPositionKeys.Products
    if (pathname === '/products') saveScrollYPosition({ sessionStorageKey })
    else saveScrollYPosition({ sessionStorageKey, position: 0 })

    productDetailsNavigate()
  }

  const cartUpdated = React.useCallback(() => {
    toast.dismiss('cart-updated')
    setTimeout(() => {
      toast('Cart updated', {
        toastId: 'cart-updated',
        type: 'warning',
        icon: <MaterialSymbol>check_circle</MaterialSymbol>,
      })
    }, 1000)
  }, [item as CartItem])

  React.useEffect(() => {
    window.addEventListener('cartUpdated', cartUpdated)
    return () => {
      window.removeEventListener('cartUpdated', cartUpdated)
    }
  }, [cartUpdated])

  React.useEffect(() => {
    if (condensed) {
      setIsPreviouslyOrdered(false)
      return
    }
    if (isAllProduct) setIsPreviouslyOrdered(itemNumbers.has(item.itemNumber))
  }, [item.itemNumber, itemNumbers, isAllProduct, condensed])

  const totalOrderedQty = React.useMemo(
    () =>
      item.orders
        ?.map((x) => x.quantity)
        .reduce((accumulator, current) => accumulator + current, 0),
    [item.orders],
  )

  const renderLastOrdered = React.useMemo(() => {
    if (isValidDate(lastOrderedDate)) {
      return (
        <ItemContainer>
          <ItemLabel variant='caption' align='right' sx={{ textTransform: 'uppercase' }}>
            Last ordered
          </ItemLabel>
          <ItemLabel
            variant='caption'
            align='right'
            data-testid={TestIds.feat.product.card.lastOrdered}
          >
            {dayjs(lastOrderedDate).format('DD MMM YYYY')}
          </ItemLabel>
        </ItemContainer>
      )
    }
  }, [lastOrderedDate])

  const helpText = React.useMemo(() => {
    let text = `You've ordered this product before.`
    if (item.hasVariants) text = `You've ordered a variation of this product before.`
    return <Typography variant='caption'>{text}</Typography>
  }, [isPreviouslyOrdered])

  function onRemove() {
    if (currentAccount) {
      dispatch(
        CartSlice.actions.removeItem({
          accountNumber: currentAccount.accountNumber,
          product: item as CartItem,
        }),
      )
      toast('Item Removed', {
        type: 'success',
        icon: <MaterialSymbol>check_circle</MaterialSymbol>,
      })
    }
  }

  const [help, setHelp] = React.useState(false)

  const productActions = React.useMemo(() => {
    if (enableQuote && quoteProvider.isQuoteable(item) && !currentAccount?.hasTfoAccess) {
      return (
        <ActionButton
          onClick={() => {
            quoteProvider.setProduct(item)
            quoteProvider.setQuoteType(QuoteModalType.QUOTE_SINGLE)
          }}
          data-testid={TestIds.feat.product.card.requestQuoteButton}
          title='Request a quote'
        />
      )
    }
  }, [
    isDisabled,
    isEndOfLife,
    quoteProvider,
    enableQuote,
    item.isPriceNegotiable,
    item.productId,
    help,
    setHelp,
  ])

  const isAlternativeProductAvailable = React.useMemo(
    () => !(alternativeProductState.isDisabled || alternativeProductState.isEndOfLife),
    [alternativeProductState],
  )

  const renderHelpMessage = React.useMemo(() => {
    const altProduct = item.alternativeProduct
    if (altProduct && isAlternativeProductAvailable) {
      return (
        <AlternativeProduct
          title={isEndOfLife ? 'This product is similar to:' : 'This product has been replaced by:'}
          item={altProduct}
          contactInfo={contactInfo}
          show={help}
        />
      )
    } else {
      return (
        <HelperMessage
          title="We're here to help"
          body='Contact your Farmlands representative to learn about other available products.'
          show={help}
          name={contactInfo.fullName}
          phone={contactInfo.phone}
          email={contactInfo.email}
        />
      )
    }
  }, [item.alternativeProduct, isAlternativeProductAvailable, isEndOfLife, contactInfo, help])

  return (
    <ProductCardContainer>
      <Card
        data-testid={testId ?? TestIds.feat.product.card.card}
        data-id={item.productId}
        data-line-id={isCartItem(item) ? item.lineId : item.productId}
        variant='outlined'
        disableLink={isDisabled || disableLink}
      >
        <ProductCardContent>
          {currentAccount && showRemove && <RemoveButton onRemove={onRemove} />}

          {showImage && (
            <ImageWrapper>
              <ImageContainer
                src={item.imageUrl}
                placeholderImageUrl={PlaceholderImage}
                data-testid={TestIds.feat.product.card.image}
                alt=''
              />
              {isSmall && renderLastOrdered}
            </ImageWrapper>
          )}
          <ProductContentContainer>
            <TopDownWrapper>
              <CardAction
                disabled={isDisabled || disableLink}
                tabIndex={-1}
                disableRipple
                sx={{ p: 0 }}
                onClick={(e) => handleClickOnTitle(e)}
              >
                <CardTitle
                  title={item.name}
                  showIcon={!isDisabled && !disableLink}
                  testId={testId ? `${testId}-name` : TestIds.feat.product.card.name}
                />
                <ProductHeaderDetails
                  itemNumber={item.itemNumber}
                  items={getProductPrices({ item })}
                  noPriceAvailable={noPriceAvailable}
                  condensed={condensed}
                  productId={item.productId?.toString()}
                  hasVariants={item.hasVariants}
                />
                {isPreviouslyOrdered && helpText}
              </CardAction>
              {!isAllProduct && (
                <ProductVariantDetails
                  key={'variants'}
                  item={item}
                  condensed={condensed}
                  totalOrderedQty={totalOrderedQty}
                />
              )}
              {isSmall && renderQuickOrderButton}
              {!condensed && productActions}
            </TopDownWrapper>
          </ProductContentContainer>
          {renderSideContainer}
        </ProductCardContent>
        {renderHelpMessage}
      </Card>
    </ProductCardContainer>
  )
}
